import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Image as ImageIcon, Camera } from 'lucide-react';
import { validateImageFile } from '@/lib/utils/imageOptimization';

interface MenuItemImageUploadProps {
  /**
   * Current image URL (if editing existing item)
   */
  currentImageUrl?: string;
  /**
   * Called when user selects a new image
   */
  onImageSelect: (file: File | null) => void;
  /**
   * Called when image upload fails
   */
  onUploadError?: (error: string) => void;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * MenuItemImageUpload - Specialized image upload for menu items
 * 
 * Features:
 * - Drag and drop upload
 * - Image optimization and compression
 * - Preview with edit options
 * - Upload progress tracking
 * - Automatic validation
 */
export const MenuItemImageUpload: React.FC<MenuItemImageUploadProps> = ({
  currentImageUrl,
  onImageSelect,
  onUploadError,
  required = false,
  className = '',
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    // Quick validation first
    const quickValidation = validateImageFile(file);
    if (!quickValidation.isValid) {
      onUploadError?.(quickValidation.error || 'Invalid file');
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    onImageSelect(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
    onImageSelect(null);
  };

  const displayImage = preview || currentImageUrl;

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Image {required && <span className="text-red-500">*</span>}
        </label>
        {displayImage && (
          <button
            type="button"
            onClick={removeImage}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        )}
      </div>      {displayImage ? (
        // Image Preview
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-gray-50 rounded-xl overflow-hidden border-2 border-gray-200"
        >
          <div className="aspect-video">
            <img
              src={displayImage}
              alt="Menu item preview"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {selectedFile ? selectedFile.name : 'Current image'}
                </p>
                {selectedFile && (
                  <p className="text-xs text-gray-500">
                    {Math.round(selectedFile.size / 1024)}KB
                  </p>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-primary00 hover:text-primary font-medium"
                >
                  Change
                </button>
                <button
                  type="button"
                  onClick={removeImage}
                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        // Upload Area
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragActive
              ? 'border-primary/60 bg-primary/10'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <ImageIcon className="w-8 h-8 text-gray-400" />
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Add menu item image
          </h3>
          <p className="text-gray-600 mb-6">
            Drag & drop an image here, or click to browse
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <Upload className="w-4 h-4 mr-2" />
              Choose File
            </button>
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:hidden"
            >
              <Camera className="w-4 h-4 mr-2" />
              Take Photo
            </button>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileSelect(file);
          }
        }}
        className="hidden"
      />
    </div>
  );
};

export default MenuItemImageUpload;